import { Link } from "react-router-dom";
import "../formulaireAbsence.css";

/**
 * FormConfirmation
 *
 * Props:
 *   headerTitle – text shown in the gradient header (form name)
 *   headerSub   – subtitle shown in the gradient header
 *   dateSent    – formatted date/time string shown as "Date d'envoi"
 *   details     – array of { label, value, icon? } shown in the summary grid
 */
const FormConfirmation = ({
  headerTitle,
  headerSub,
  dateSent,
  details,
}) => {
  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#f9f9ff",
        fontFamily: "Inter, sans-serif",
      }}
    >
      {/* Gradient header — same style as all forms */}
      <div
        style={{
          background: "linear-gradient(135deg, #00447f 0%, #0066cc 100%)",
          padding: "1.25rem 1.5rem",
          display: "flex",
          alignItems: "center",
          gap: "1rem",
          flexWrap: "wrap",
          boxShadow: "0 2px 12px rgba(0,68,127,0.18)",
        }}
      >
        <Link to="/">
          <img
            src="/logo.jpg"
            alt="logo"
            style={{
              maxHeight: "56px",
              width: "auto",
              borderRadius: "0.5rem",
              backgroundColor: "rgba(255,255,255,0.95)",
              padding: "0.25rem",
              flexShrink: 0,
            }}
          />
        </Link>
        <div style={{ minWidth: 0 }}>
          <h1
            style={{
              color: "#ffffff",
              fontSize: "1.2rem",
              fontWeight: 700,
              margin: 0,
              lineHeight: 1.2,
            }}
          >
            {headerTitle}
          </h1>
          <p
            style={{
              color: "rgba(255,255,255,0.8)",
              fontSize: "0.85rem",
              margin: "0.25rem 0 0 0",
            }}
          >
            {headerSub}
          </p>
        </div>
      </div>

      {/* Page content */}
      <div
        style={{
          maxWidth: "48rem",
          margin: "0 auto",
          padding: "3rem 1.25rem 5rem",
        }}
      >
        {/* Success icon + heading */}
        <div style={{ textAlign: "center", marginBottom: "2.5rem" }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: "5rem",
              height: "5rem",
              borderRadius: "9999px",
              backgroundColor: "#f0fdf4",
              marginBottom: "1.25rem",
            }}
          >
            <span
              className="material-symbols-outlined"
              style={{
                fontSize: "3rem",
                color: "#16a34a",
                fontVariationSettings: "'FILL' 1",
              }}
            >
              check_circle
            </span>
          </div>
          <h2
            style={{
              fontSize: "clamp(1.5rem, 4vw, 2rem)",
              fontWeight: 800,
              color: "#00447f",
              fontFamily: "Public Sans, sans-serif",
              margin: "0 0 0.75rem 0",
              letterSpacing: "-0.02em",
            }}
          >
            Votre demande a été soumise avec succès
          </h2>
        </div>

        {/* Summary card */}
        <div
          style={{
            backgroundColor: "#ffffff",
            borderRadius: "0.875rem",
            padding: "2rem",
            boxShadow: "0 8px 32px rgba(24,28,35,0.07)",
            border: "1px solid rgba(193,198,211,0.4)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Decorative blur circle */}
          <div
            style={{
              position: "absolute",
              top: "-3rem",
              right: "-3rem",
              width: "10rem",
              height: "10rem",
              borderRadius: "9999px",
              backgroundColor: "rgba(212,227,255,0.35)",
              filter: "blur(2rem)",
              pointerEvents: "none",
            }}
          />

          <div style={{ position: "relative" }}>
            {/* Confirmation number + status row */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: "1rem",
                paddingBottom: "1.5rem",
                marginBottom: "1.75rem",
                borderBottom: "1.5px solid #e6e8f2",
              }}
            >
              <div>
                <span
                  style={{
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    color: "#60626b",
                    display: "block",
                    marginBottom: "0.25rem",
                  }}
                >
                  Date d'envoi
                </span>
                <p
                  style={{
                    fontSize: "1.15rem",
                    fontWeight: 700,
                    color: "#00447f",
                    fontFamily: "Public Sans, sans-serif",
                    margin: 0,
                  }}
                >
                  {dateSent}
                </p>
              </div>
              <div
                style={{
                  backgroundColor: "#f2f3fe",
                  padding: "0.5rem 1rem",
                  borderRadius: "0.5rem",
                }}
              >
                <span
                  style={{
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "#414751",
                    display: "block",
                    marginBottom: "0.2rem",
                  }}
                >
                  Statut
                </span>
                <span
                  style={{
                    fontSize: "0.9rem",
                    fontWeight: 700,
                    color: "#005ca8",
                  }}
                >
                  Envoyé
                </span>
              </div>
            </div>

            {/* Details grid */}
            <div className="confirmation-grid" style={{ gap: "1.75rem 2rem" }}>
              {details.map((item, i) => (
                <div key={i} style={{ minWidth: 0 }}>
                  <span
                    style={{
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      color: "#414751",
                      display: "block",
                      marginBottom: "0.35rem",
                    }}
                  >
                    {item.label}
                  </span>
                  {item.icon && (
                    <span
                      className="material-symbols-outlined"
                      style={{
                        fontSize: "1.1rem",
                        color: "#00447f",
                        verticalAlign: "middle",
                        marginRight: "0.35rem",
                      }}
                    >
                      {item.icon}
                    </span>
                  )}
                  <p
                    style={{
                      fontSize: "1rem",
                      fontWeight: 600,
                      color: "#181c23",
                      margin: 0,
                      wordBreak: "break-word",
                    }}
                  >
                    {item.value || "—"}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div
          style={{
            marginTop: "2.5rem",
            display: "flex",
            flexDirection: "column",
            gap: "0.875rem",
            alignItems: "stretch",
          }}
        >
          <Link
            to="/"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.6rem",
              backgroundColor: "#00447f",
              color: "#ffffff",
              padding: "0.875rem 2rem",
              borderRadius: "0.625rem",
              fontWeight: 600,
              fontSize: "0.95rem",
              textDecoration: "none",
              transition: "background-color 0.15s",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor = "#005ca8")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = "#00447f")
            }
          >
            <span
              className="material-symbols-outlined"
              style={{ fontSize: "1.1rem" }}
            >
              home
            </span>
            Retour à l'accueil
          </Link>
        </div>
      </div>
    </div>
  );
};

export default FormConfirmation;
